export const AVERAGE_ACTIONS = ["Buy", "Sell", "Short", "Cover"];


const pad = (n) => String(n).padStart(2, "0");

export const toISODate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Previous business day (Mon -> Fri). Market holidays are not considered.
export const prevBusinessDay = (from = new Date()) => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  do {
    d.setDate(d.getDate() - 1);
  } while (d.getDay() === 0 || d.getDay() === 6);
  return d;
};

export const findAverage = (rows, symbol, action) =>
  (rows || []).find((r) => r.symbol === symbol && r.action === action) || null;

export const uniqueSymbols = (rows) =>
  [...new Set((rows || []).map((r) => r.symbol))].sort();

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// cash + ratio * leg2 - leg1. Cash may be blank (0); ratio and both leg
// averages are required, otherwise null.
export const spreadPrice = ({ cash, ratio, leg1Avg, leg2Avg }) => {
  const r = toNumber(ratio);
  const c = toNumber(cash) ?? 0;
  const l1 = toNumber(leg1Avg);
  const l2 = toNumber(leg2Avg);
  if (r == null || l1 == null || l2 == null) return null;
  return c + r * l2 - l1;
};

// Spread price for each trade date, newest first. dateRows are the per-date
// averages of the two legs ({ date, symbol, avg_price, quantity }). A date
// where only one leg traded has no spread (null), but is still listed.
export const spreadByDate = ({
  dateRows,
  targetSymbol,
  acquirerSymbol,
  cash,
  ratio,
}) => {
  const days = [...new Set((dateRows || []).map((r) => r.date))].sort().reverse();
  return days.map((date) => {
    const target = dateRows.find((r) => r.date === date && r.symbol === targetSymbol);
    const acquirer = dateRows.find((r) => r.date === date && r.symbol === acquirerSymbol);
    return {
      date,
      targetAvg: target?.avg_price ?? null,
      targetQty: target?.quantity ?? 0,
      acquirerAvg: acquirer?.avg_price ?? null,
      acquirerQty: acquirer?.quantity ?? 0,
      spread: spreadPrice({
        cash,
        ratio,
        leg1Avg: target?.avg_price,
        leg2Avg: acquirer?.avg_price,
      }),
    };
  });
};

// One average per symbol across all of its Buy, Sell, Short and Cover trades
// in the result (quantity weighted), or null when it has none.
export const symbolAverage = (rows, symbol) => {
  let weighted = 0;
  let quantity = 0;
  let trades = 0;
  for (const r of rows || []) {
    if (r.symbol !== symbol || r.avg_price == null) continue;
    weighted += r.avg_price * r.quantity;
    quantity += r.quantity;
    trades += r.trades || 0;
  }
  return quantity ? { avg_price: weighted / quantity, quantity, trades } : null;
};

// Runs the spread calculation for the Calculate button. Target is leg 1 and
// acquirer is leg 2; each uses the symbol's average across all of its trades
// under the current filter. Returns either { error } or
// { targetAvg, acquirerAvg, spread } so the UI can say exactly what is missing.
export const calculateSpread = ({
  rows,
  targetSymbol,
  acquirerSymbol,
  cash,
  ratio,
}) => {
  if (!targetSymbol || !acquirerSymbol) {
    return { error: "Select both the target and the acquirer." };
  }
  if (toNumber(ratio) == null) {
    return { error: "Enter a ratio." };
  }
  if (cash !== "" && cash != null && toNumber(cash) == null) {
    return { error: "Cash must be a number." };
  }
  const target = symbolAverage(rows, targetSymbol);
  const acquirer = symbolAverage(rows, acquirerSymbol);
  if (!target) {
    return { error: `No priced trades for ${targetSymbol} in this result.` };
  }
  if (!acquirer) {
    return { error: `No priced trades for ${acquirerSymbol} in this result.` };
  }
  return {
    targetAvg: target.avg_price,
    acquirerAvg: acquirer.avg_price,
    spread: spreadPrice({
      cash,
      ratio,
      leg1Avg: target.avg_price,
      leg2Avg: acquirer.avg_price,
    }),
  };
};

// Averages are only offered once the result is narrowed by content: a search
// of 3+ characters (what the list endpoint actually sends) or one of the
// strategy / action / security type / broker filters. A date range alone is
// the whole archive for that period, which is too big to average usefully.
export const hasActiveFilter = (query, filters = {}) =>
  String(query ?? "").trim().length >= 3 ||
  Boolean(
    filters.strategyType ||
      filters.posAction ||
      filters.secType ||
      filters.brokerCode,
  );
